import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';

type RoomVote = { score: number | null; pending: boolean; revision: number; uncertain?: { score: number; body: string } };
export type RoomVoteResponse = { attentionScore?: number | null; signalDisposition?: { roomId: number; state: string } | null };
type VoteTarget = { roomId: number; outputType: string; outputId: number | string };
export const roomPriorityQueryKey = (roomId: number) => ['/api/exai/ratings', { roomId }] as const;
const emptyVote = (): RoomVote => ({ score: null, pending: false, revision: 0 });
const responseScore = (response: RoomVoteResponse | null): number | null => {
  const score = response?.attentionScore;
  return typeof score === 'number' && Number.isInteger(score) && score >= 0 && score <= 100 ? score : null;
};

export async function readRoomPriority(client: QueryClient, target: VoteTarget): Promise<RoomVote> {
  const key = roomPriorityQueryKey(target.roomId);
  const revision = client.getQueryData<RoomVote>(key)?.revision ?? 0;
  const params = new URLSearchParams({ outputType: target.outputType, outputId: String(target.outputId) });
  const response = await apiClient(`/api/exai/ratings?${params}`) as RoomVoteResponse | null;
  const current = client.getQueryData<RoomVote>(key);
  // A delayed GET must never undo a gesture made on either surface.
  if (current && (current.pending || current.revision !== revision)) return current;
  return { score: responseScore(response), pending: false, revision, uncertain: current?.uncertain };
}

export async function saveRoomPriority(client: QueryClient, target: VoteTarget, score: number, context: Record<string, unknown>): Promise<RoomVoteResponse | null> {
  const key = roomPriorityQueryKey(target.roomId);
  const previous = client.getQueryData<RoomVote>(key) ?? emptyVote();
  if (previous.pending) return null;
  const revision = previous.revision + 1;
  // Reserve synchronously: pointerup/touchend and two mounted controls share
  // one pending gesture. Its request body (including id) is stable on retry.
  const body = previous.uncertain?.score === score
    ? previous.uncertain.body
    : JSON.stringify({ ...target, attentionScore: score, context, gestureId: crypto.randomUUID() });
  client.setQueryData(key, { score, pending: true, revision });
  await client.cancelQueries({ queryKey: key }, { revert: false });
  try {
    const response = await apiClient('/api/exai/ratings', { method: 'POST', body }) as RoomVoteResponse;
    if (client.getQueryData<RoomVote>(key)?.revision === revision) {
      client.setQueryData(key, { score: responseScore(response) ?? score, pending: false, revision });
    }
    void client.invalidateQueries({ queryKey: ['/api/exai/rooms'] });
    void client.invalidateQueries({ queryKey: ['/api/exai/rooms/focus'] });
    return response;
  } catch (error) {
    if (client.getQueryData<RoomVote>(key)?.revision === revision) {
      const status = (error as { status?: unknown } | null)?.status;
      // Missing response/timeout/5xx can follow a committed server vote. Keep
      // only this operation for an explicit same-value retry. A different
      // gesture replaces it; success clears it, including 30 -> 40 -> 30.
      const uncertain = typeof status !== 'number' || status === 0 || status >= 500;
      client.setQueryData(key, { score: previous.score, pending: false, revision,
        uncertain: uncertain ? { score, body } : undefined });
    }
    throw error;
  }
}

/** UI state only, in the existing query cache; no new Martina data channel. */
export function useMartinaRoomPriority(roomId: number | null, outputType: string, outputId: number | string) {
  const client = useQueryClient();
  const target = { roomId: roomId ?? 0, outputType, outputId };
  const query = useQuery({
    queryKey: roomPriorityQueryKey(target.roomId),
    queryFn: () => readRoomPriority(client, target),
    enabled: roomId != null, staleTime: 60_000, retry: false, refetchOnWindowFocus: false,
  });
  return {
    score: query.data?.score ?? null,
    resolved: query.data !== undefined,
    saving: query.data?.pending === true,
    loading: roomId != null && query.isPending,
    save: (score: number, context: Record<string, unknown>) => saveRoomPriority(client, target, score, context),
  };
}
